/**
 * Internationalization (i18n) Interfaces
 * Defines contracts for multi-language support across the platform
 */

export type SupportedLanguage = 'en' | 'pt-BR' | 'pt-PT' | 'es';
export type SupportedRegion = 'US' | 'BR' | 'PT' | 'ES' | 'MX' | 'AR';

export interface I18nConfig {
  defaultLanguage: SupportedLanguage;
  supportedLanguages: SupportedLanguage[];
  fallbackLanguage: SupportedLanguage;
  detection: LanguageDetectionConfig;
}

export interface LanguageDetectionConfig {
  order: DetectionMethod[];
  caches: string[];
  cookieName?: string;
  lookupQuerystring?: string;
  lookupCookie?: string;
  lookupLocalStorage?: string;
  lookupSessionStorage?: string;
  htmlTag?: boolean;
}

export type DetectionMethod = 
  | 'querystring'
  | 'cookie'
  | 'localStorage'
  | 'sessionStorage'
  | 'navigator'
  | 'htmlTag'
  | 'path'
  | 'subdomain'
  | 'cloudflare-geo';

export interface TranslationResource {
  [namespace: string]: {
    [key: string]: string | TranslationResource;
  };
}

export interface LanguagePack {
  language: SupportedLanguage;
  region: SupportedRegion;
  translations: {
    common: TranslationResource;
    navigation: TranslationResource;
    marketing: TranslationResource;
    dashboard: TranslationResource;
    auth: TranslationResource;
    educational: TranslationResource;
    errors: TranslationResource;
    validation: TranslationResource;
  };
  metadata: LanguageMetadata;
}

export interface LanguageMetadata {
  displayName: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
  dateFormat: string;
  timeFormat: string;
  numberFormat: NumberFormatConfig;
  currency: CurrencyConfig;
}

export interface NumberFormatConfig {
  decimal: string;
  thousands: string;
  grouping: number[];
}

export interface CurrencyConfig {
  code: string;
  symbol: string;
  position: 'before' | 'after';
  decimal: string;
  thousands: string;
  precision: number;
}

export interface LocalizedContent {
  id: string;
  language: SupportedLanguage;
  content: any;
  metadata: {
    lastModified: string;
    version: string;
    status: 'draft' | 'published';
  };
}

export interface I18nKVStorageKey {
  namespace: 'i18n';
  language: SupportedLanguage;
  type: 'translations' | 'content' | 'metadata';
  key: string;
}

export interface LanguageSwitcherProps {
  currentLanguage: SupportedLanguage;
  onLanguageChange: (language: SupportedLanguage) => void;
  showFlags?: boolean;
  showNativeNames?: boolean;
}

export interface LocalizedSEOMetadata {
  language: SupportedLanguage;
  title: string;
  description: string;
  keywords: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonicalUrl?: string;
  alternateLanguages?: Array<{
    language: SupportedLanguage;
    url: string;
  }>;
}

export interface RegionalCustomization {
  region: SupportedRegion;
  language: SupportedLanguage;
  currency: CurrencyConfig;
  pricing: {
    currency: string;
    taxRate: number;
    includesTax: boolean;
  };
  legal: {
    privacyPolicyUrl: string;
    termsOfServiceUrl: string;
    cookiePolicyUrl: string;
  };
  support: {
    phone?: string;
    email: string;
    workingHours: string;
    timezone: string;
  };
}

export interface I18nContextValue {
  language: SupportedLanguage;
  region: SupportedRegion;
  t: (key: string, options?: any) => string;
  changeLanguage: (language: SupportedLanguage) => Promise<void>;
  changeRegion: (region: SupportedRegion) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

export interface I18nApiResponse {
  language: SupportedLanguage;
  region: SupportedRegion;
  translations: TranslationResource;
  metadata: LanguageMetadata;
  regional: RegionalCustomization;
}

// KV Storage Keys for i18n
export const I18N_KV_KEYS = {
  TRANSLATIONS: (lang: SupportedLanguage) => `i18n:translations:${lang}`,
  CONTENT: (lang: SupportedLanguage, contentId: string) => `i18n:content:${lang}:${contentId}`,
  METADATA: (lang: SupportedLanguage) => `i18n:metadata:${lang}`,
  REGIONAL: (region: SupportedRegion) => `i18n:regional:${region}`,
} as const;

// Language configuration constants
export const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'pt-BR', 'pt-PT', 'es'];
export const SUPPORTED_REGIONS: SupportedRegion[] = ['US', 'BR', 'PT', 'ES', 'MX', 'AR'];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';
export const DEFAULT_REGION: SupportedRegion = 'US';

// Language to region mapping (primary region for each language)
export const LANGUAGE_REGION_MAP: Record<SupportedLanguage, SupportedRegion> = {
  en: 'US',
  'pt-BR': 'BR',
  'pt-PT': 'PT',
  es: 'ES',
};

// Region to language mapping
export const REGION_LANGUAGE_MAP: Record<SupportedRegion, SupportedLanguage> = {
  US: 'en',
  BR: 'pt-BR',
  PT: 'pt-PT',
  ES: 'es',
  MX: 'es',
  AR: 'es',
};